import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { CHAT_UI_MAX_OFFERS } from '../../shared/schema/limits.js';
import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';

/**
 * Uma oferta de mobilidade — corrida de cooperativa (valor previsto) OU carro
 * com motorista (diária). `offerId` é opaco, nunca renderizado: ecoa de volta
 * pelo `mobility-select` para o agente resolver por id. `unitLabel` é o rótulo
 * de unidade do carro com motorista ("/diária"); o da corrida é o «valor
 * previsto» do próprio card. `outOfPolicy` é legado (cartões já gravados): a
 * política por distância vale para a corrida inteira e mora no bloco
 * (`distanceOverLimit`), não na linha.
 *
 * Campos display-only opcionais (projeção determinística do FSM, nunca prosa do
 * LLM), presentes só quando o provider os fornece — o card degrada quando ausentes:
 *  - `distanceKm` (cooperativa): distância da rota (`distancePolicy.km`), igual em
 *    todas as corridas — contexto do trajeto; com `roundTrip`, por trecho.
 *  - `hours` / `days` (carro c/ motorista): horas/dia da franquia e nº de diárias
 *    assumidas. Com `days`, `price` é o TOTAL das diárias (diária × `days`) e o
 *    card mostra a diária (`price / days`) em destaque, com "por diária", e o
 *    total no rótulo; sem `days`, `price` é uma diária só. Com preço, o carro
 *    com motorista não mostra o `unitLabel`.
 *  - `franchiseKm` / `freeKm` (carro c/ motorista): a franquia de km da diária
 *    na tarifa — "Diária 10h com 100 km" ou "Diária 10h com km livre". Sem
 *    nenhum dos dois, as horas aparecem como "10h/dia".
 *  - `vehicle`: a categoria do veículo ("Sedan executivo"), ao lado do título.
 */
export const ChatUiMobilityOfferSchema = z
  .object({
    offerId: z.string().min(1),
    section: z.enum(['cooperative', 'car_driver']),
    title: z.string().min(1),
    price: ChatUiMoneySchema.optional(),
    unitLabel: z.string().optional(),
    outOfPolicy: z.boolean().optional(),
    vehicle: z.string().optional(),
    distanceKm: z.number().nonnegative().optional(),
    hours: z.number().int().positive().optional(),
    days: z.number().int().positive().optional(),
    franchiseKm: z.number().nonnegative().optional(),
    freeKm: z.boolean().optional(),
  })
  .strict();
export type ChatUiMobilityOffer = z.infer<typeof ChatUiMobilityOfferSchema>;

export const MobilityOffersBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('mobility_offers'),
    offers: z.array(ChatUiMobilityOfferSchema).min(1).max(CHAT_UI_MAX_OFFERS),
    // Variante de exibição. `collapsed` = lista compacta pós-seleção (somente
    // leitura, sem CTA, selecionado no topo). O agente a re-emite com o MESMO
    // `id` do card de busca → o web-chat atualiza o card in-place (encolhe) em
    // vez de empilhar uma cópia nova. Espelha `rental_offers.variant`.
    variant: z.enum(['collapsed']).optional(),
    // Ida e volta (#6699): o `price` da corrida é o total previsto das 2 corridas
    // e a distância é por trecho.
    roundTrip: z.boolean().optional(),
    // A corrida passa do limite de distância da empresa (#6699): o aviso vai no
    // topo das opções, para a corrida inteira. `km` é a distância da rota;
    // `limitKm`, o limite configurado — ausente quando a API não o informa.
    // Vale só para a cooperativa: o carro com motorista não tem rota.
    distanceOverLimit: z
      .object({ km: z.number().positive(), limitKm: z.number().positive().optional() })
      .strict()
      .optional(),
    // `offerId` da oferta escolhida — fixada no topo e destacada na variante
    // collapsed. Ignorada quando ausente / fora da lista.
    selectedOfferId: z.string().min(1).optional(),
  })
  .strict();
export type MobilityOffersBlock = z.infer<typeof MobilityOffersBlockSchema>;
